import React from 'react';
import { Box, ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useSpace from 'src/hooks/useSpace';
import { adminTokens } from 'src/views/common/adminTokens';
import PhosphorIcon from 'src/views/common/PhosphorIcon';

function markLabel(name?: string) {
    if (!name) return '';
    return name.trim().slice(0, 3).toLowerCase();
}

const rowSx = {
    padding: '6px 12px',
    borderRadius: adminTokens.radius,
    cursor: 'pointer',
    minHeight: 44,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:hover': { background: adminTokens.gray100 },
};

const SpaceSelection = () => {
    const { user } = useAuth();
    const [space, setSpace] = useSpace();
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const [open, setOpen] = React.useState(false);

    const spaces = React.useMemo(() => {
        if (!user) return [];
        return [...(user.personalSpaces || []), ...(user.contributedSpaces || [])];
    }, [user]);

    const currentName = space?.name || user?.name || '';
    const currentRole = space ? space.user_role : 'Personal';

    const handleSelect = (id: string | null) => {
        setSpace(id);
        setOpen(false);
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                component="button"
                type="button"
                ref={anchorRef}
                onClick={() => setOpen((o) => !o)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, height: 44, padding: '0 10px',
                    borderRadius: adminTokens.radiusMd, cursor: 'pointer', border: 'none', background: 'none',
                    fontFamily: adminTokens.fontSans, textAlign: 'left', maxWidth: 220,
                    '&:hover': { background: adminTokens.gray100 },
                }}
            >
                <Box sx={{
                    width: 26, height: 26, borderRadius: '6px', background: adminTokens.navy800, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
                    flexShrink: 0, textTransform: 'lowercase',
                }}>
                    {markLabel(currentName) || 'me'}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', minWidth: 0 }}>
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: adminTokens.navy800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>
                        {currentName}
                    </Box>
                    <Box sx={{ fontSize: 9, fontWeight: 600, color: adminTokens.gray500, textTransform: 'uppercase', letterSpacing: '.03em', lineHeight: 1 }}>
                        {currentRole}
                    </Box>
                </Box>
                <PhosphorIcon name="caret-down" size={12} color={adminTokens.gray500} />
            </Box>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" transition style={{ zIndex: 1300 }}>
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                        <Paper sx={{
                            mt: 0.5, minWidth: 260, borderRadius: adminTokens.radiusMd,
                            border: `1px solid ${adminTokens.gray200}`, boxShadow: adminTokens.shadowDropdown, p: 0.5,
                        }}>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <Box>
                                    <Box onClick={() => handleSelect(null)} sx={{ ...rowSx, background: space ? 'transparent' : adminTokens.gray100 }}>
                                        <Box sx={{ fontSize: adminTokens.textBase, fontWeight: 600, color: adminTokens.navy800, lineHeight: 1.3 }}>
                                            {user?.name}
                                        </Box>
                                        <Box sx={{ fontSize: 9, fontWeight: 500, color: adminTokens.gray500, textTransform: 'uppercase', letterSpacing: '.03em', mt: '2px' }}>
                                            Personal
                                        </Box>
                                    </Box>

                                    {spaces.length > 0 && (
                                        <>
                                            <Box sx={{ height: '1px', background: adminTokens.gray200, my: '4px' }} />
                                            <Box sx={{ fontSize: adminTokens.textXs, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: adminTokens.gray500, padding: '6px 12px' }}>
                                                Spaces
                                            </Box>
                                            {spaces.map((s: any) => (
                                                <Box
                                                    key={s.id}
                                                    onClick={() => handleSelect(s.id)}
                                                    sx={{ ...rowSx, background: space?.id === s.id ? adminTokens.gray100 : 'transparent' }}
                                                >
                                                    <Box sx={{ fontSize: adminTokens.textBase, fontWeight: 600, color: adminTokens.navy800, lineHeight: 1.3 }}>
                                                        {s.name}
                                                    </Box>
                                                    <Box sx={{ fontSize: 9, fontWeight: 500, color: adminTokens.gray500, textTransform: 'uppercase', letterSpacing: '.03em', mt: '2px' }}>
                                                        {s.user_role}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};

export default SpaceSelection;
